/**
 * LanguageSwitcher — dropdown w header'ach Landing / AdminPanel.
 *
 * Aktualnie tylko PL działa. Kliknięcie EN / HR wyświetla toast
 * "W przygotowaniu" — user widzi że aplikacja przygotowana na
 * wielojęzyczność, bez frustracji "dlaczego to nie działa".
 *
 * Gdy dodasz drugie locale (np. `en.json`):
 *   1. `import en from "./locales/en.json"` w /app/frontend/src/i18n/index.js
 *   2. `resources: { pl: {...}, en: { translation: en } }`
 *   3. Ustaw `active: true` dla EN w LANGUAGES poniżej
 *   4. Kliknięcie po prostu wywoła i18n.changeLanguage(code).
 */
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LANGUAGES = [
  { code: "pl", flag: "🇵🇱", label: "Polski",   active: true  },
  { code: "en", flag: "🇬🇧", label: "English",  active: false },
  { code: "hr", flag: "🇭🇷", label: "Hrvatski", active: false },
];

export default function LanguageSwitcher({ variant = "compact" }) {
  const { t, i18n } = useTranslation();
  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const handleSelect = (lang) => {
    if (lang.active) {
      i18n.changeLanguage(lang.code);
      return;
    }
    toast.info(t("common.language_switcher.coming_soon_title"), {
      description: t("common.language_switcher.coming_soon_desc"),
      duration: 3500,
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="brut brut-hover bg-white border-2 border-[#0F172A] rounded-md
                     px-2 py-1 text-sm font-bold flex items-center gap-1.5
                     hover:bg-[#FEF08A] transition-colors"
          data-testid="language-switcher-trigger"
          aria-label={t("common.language_switcher.label")}
          title={t("common.language_switcher.label")}
        >
          <Globe className="w-4 h-4" strokeWidth={2.5} />
          <span aria-hidden>{current.flag}</span>
          {variant !== "flag-only" && (
            <span className="hidden sm:inline uppercase tracking-wider text-xs">
              {current.code}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="brut border-2 border-[#0F172A] rounded-md bg-white p-1"
        data-testid="language-switcher-menu"
      >
        {LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onSelect={() => handleSelect(lang)}
            className={`brut-hover cursor-pointer font-bold text-sm rounded-sm
                        flex items-center gap-2 px-2 py-1.5
                        ${lang.code === current.code ? "bg-[#FEF08A]" : ""}`}
            data-testid={`language-option-${lang.code}`}
          >
            <span className="text-base leading-none" aria-hidden>{lang.flag}</span>
            <span>{lang.label}</span>
            {!lang.active && (
              <span className="ml-auto text-[10px] uppercase tracking-wider
                               text-[#64748B] font-bold">
                soon
              </span>
            )}
            {lang.code === current.code && (
              <span className="ml-auto text-[10px] uppercase tracking-wider
                               text-[#0F172A] font-black">
                ✓
              </span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
